import React, { useState, useEffect, useRef } from 'react';
import { Search, Send, Phone, Video, MoreVertical, UserCircle, Clock } from 'lucide-react';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    setTimeout(() => {
      setConversations([
        {
          id: 1,
          participants: ['Jana Svobodová', 'Praha Cleaning Service'],
          lastMessage: 'Děkuji za vynikající službu!',
          lastMessageTime: '14:30',
          unreadCount: 2,
          status: 'active',
          avatar: null,
          type: 'customer-provider'
        },
        {
          id: 2,
          participants: ['Petr Novák', 'Brno Plumbing Masters'],
          lastMessage: 'Můžete prosím potvrdit čas?',
          lastMessageTime: '12:45',
          unreadCount: 0,
          status: 'resolved',
          avatar: null,
          type: 'customer-provider'
        },
        {
          id: 3,
          participants: ['Marie Černá', 'Ostrava Electric Solutions'],
          lastMessage: 'Potřebuji pomoc s fakturací',
          lastMessageTime: '09:15',
          unreadCount: 1,
          status: 'escalated',
          avatar: null,
          type: 'customer-provider'
        },
        {
          id: 4,
          participants: ['Tomáš Dvořák', 'Support Team'],
          lastMessage: 'Problém s přihlášením',
          lastMessageTime: '16:20',
          unreadCount: 3,
          status: 'support',
          avatar: null,
          type: 'customer-support'
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const fetchMessages = async (conversationId) => {
    // Mock messages for selected conversation
    const mockMessages = {
      1: [
        {
          id: 1,
          senderId: 'customer_1',
          senderName: 'Jana Svobodová',
          content: 'Dobrý den, chtěla bych se zeptat na dostupnost úklidu pro zítřek.',
          timestamp: '2025-01-09 10:30',
          type: 'text'
        },
        {
          id: 2,
          senderId: 'provider_1',
          senderName: 'Praha Cleaning Service',
          content: 'Dobrý den! Ano, máme volný termín zítra od 14:00. Kolik pokojů by to bylo?',
          timestamp: '2025-01-09 10:32',
          type: 'text'
        },
        {
          id: 3,
          senderId: 'customer_1',
          senderName: 'Jana Svobodová',
          content: 'Jedná se o byt 2+1, asi 65m². Kolik by to stálo?',
          timestamp: '2025-01-09 10:35',
          type: 'text'
        },
        {
          id: 4,
          senderId: 'provider_1',
          senderName: 'Praha Cleaning Service',
          content: 'Za byt této velikosti to bude 1250 Kč. Zahrnuje to úklid všech místností, koupelny a kuchyně.',
          timestamp: '2025-01-09 10:37',
          type: 'text'
        },
        {
          id: 5,
          senderId: 'customer_1',
          senderName: 'Jana Svobodová',
          content: 'Skvělé! Můžeme si domluvit termín?',
          timestamp: '2025-01-09 14:28',
          type: 'text'
        },
        {
          id: 6,
          senderId: 'provider_1',
          senderName: 'Praha Cleaning Service',
          content: 'Samozřejmě! Zítra ve 14:00 vám vyhovuje?',
          timestamp: '2025-01-09 14:29',
          type: 'text'
        },
        {
          id: 7,
          senderId: 'customer_1',
          senderName: 'Jana Svobodová',
          content: 'Děkuji za vynikající službu!',
          timestamp: '2025-01-09 14:30',
          type: 'text'
        }
      ],
      2: [
        {
          id: 1,
          senderId: 'customer_2',
          senderName: 'Petr Novák',
          content: 'Mám problém s undělným kohoutem v kuchyni.',
          timestamp: '2025-01-08 11:20',
          type: 'text'
        },
        {
          id: 2,
          senderId: 'provider_2',
          senderName: 'Brno Plumbing Masters',
          content: 'Dobrý den, můžu být u vás dnes odpoledne. Vyhovuje vám mezi 15-17?',
          timestamp: '2025-01-08 11:25',
          type: 'text'
        },
        {
          id: 3,
          senderId: 'customer_2',
          senderName: 'Petr Novák',
          content: 'Můžete prosím potvrdit čas?',
          timestamp: '2025-01-08 12:45',
          type: 'text'
        }
      ]
    };

    setMessages(mockMessages[conversationId] || []);
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation) {
      const message = {
        id: messages.length + 1,
        senderId: 'admin',
        senderName: 'Admin',
        content: newMessage,
        timestamp: new Date().toLocaleString('cs-CZ'),
        type: 'text'
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participants.some(participant =>
      participant.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const StatusBadge = ({ status }) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      resolved: 'bg-gray-100 text-gray-800',
      escalated: 'bg-red-100 text-red-800',
      support: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const MessageBubble = ({ message, isAdmin }) => (
    <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
        isAdmin 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-200 text-gray-900'
      }`}>
        <div className="text-xs opacity-75 mb-1">{message.senderName}</div>
        <div className="text-sm">{message.content}</div>
        <div className={`text-xs mt-1 ${isAdmin ? 'text-blue-200' : 'text-gray-500'}`}>
          {message.timestamp}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chat Management</h1>
          <p className="text-gray-600 mt-1">Monitor and manage customer-provider communications</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="flex items-center">
              <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
              Active: {conversations.filter(c => c.status === 'active').length}
            </span>
            <span className="flex items-center">
              <div className="h-2 w-2 bg-red-500 rounded-full mr-2"></div>
              Escalated: {conversations.filter(c => c.status === 'escalated').length}
            </span>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="grid grid-cols-12 gap-6 h-[700px]">
        {/* Conversations Sidebar */}
        <div className="col-span-12 lg:col-span-4 card p-0 overflow-hidden">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-0">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => handleConversationSelect(conversation)}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                      selectedConversation?.id === conversation.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <UserCircle className="h-10 w-10 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {conversation.participants.join(' ↔ ')}
                            </p>
                            <div className="flex items-center space-x-2">
                              {conversation.unreadCount > 0 && (
                                <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                  {conversation.unreadCount}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{conversation.lastMessageTime}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-1">{conversation.lastMessage}</p>
                          <div className="flex items-center justify-between mt-2">
                            <StatusBadge status={conversation.status} />
                            <span className="text-xs text-gray-400">{conversation.type}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="col-span-12 lg:col-span-8 card p-0 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <UserCircle className="h-8 w-8 text-gray-400" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {selectedConversation.participants.join(' ↔ ')}
                    </h3>
                    <p className="text-sm text-gray-500">{selectedConversation.type}</p>
                  </div>
                  <StatusBadge status={selectedConversation.status} />
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded">
                    <Phone className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded">
                    <Video className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
                {messages.length > 0 ? (
                  <div>
                    {messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isAdmin={message.senderId === 'admin'}
                      />
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <UserCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No messages in this conversation</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>Press Enter to send, Shift+Enter for new line</span>
                  <span className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {new Date().toLocaleTimeString('cs-CZ')}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <UserCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Conversation</h3>
                <p className="text-gray-500">Choose a conversation from the sidebar to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Conversations</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{conversations.length}</p>
            </div>
            <UserCircle className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Chats</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {conversations.filter(c => c.status === 'active').length}
              </p>
            </div>
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <div className="h-3 w-3 bg-green-600 rounded-full"></div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Escalated</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {conversations.filter(c => c.status === 'escalated').length}
              </p>
            </div>
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 font-bold">!</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">12m</p>
            </div>
            <Clock className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;